/**
 * LongTermRentalCase API Types
 *
 * Long-term rental contracts ("dlouhodoby pronajem") with monthly rates,
 * leasing info, tyres, vignette, etc. Status is derived from dates.
 */

import type { CustomerData, RentalCarData } from './rentalCase'

// ==================== Status ====================

export type LongTermCaseStatus = 'ACTIVE' | 'ENDED'

/**
 * Derive status from endDate — ENDED if endDate's calendar day is before today.
 * Uses day-granularity comparison to avoid issues with BE timezone offsets.
 */
export const deriveLongTermCaseStatus = (endDate: number): LongTermCaseStatus => {
  const endDay = new Date(endDate)
  endDay.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return endDay < today ? 'ENDED' : 'ACTIVE'
}

export const longTermCaseStatusLabels: Record<LongTermCaseStatus, string> = {
  ACTIVE: 'Aktivn\u00ed',
  ENDED: 'Ukon\u010deno'
}

export const longTermCaseStatusColors: Record<LongTermCaseStatus, string> = {
  ACTIVE: 'success',
  ENDED: 'grey'
}

// ==================== CustomData ====================

export interface LongTermCaseCustomData {
  [key: string]: unknown
}

// ==================== Main Entity ====================

export interface LongTermCase {
  id: string

  // Required (BE @DLParameter required=true)
  customer: string                       // UUID
  rentalCar: string                      // UUID
  startDate: number                      // timestamp
  endDate: number                        // timestamp
  monthlyRate: number
  deductible: number
  service: boolean
  leasingStartDate: number               // timestamp
  leasingEndDate: number                 // timestamp
  type: string
  contractNumber: string

  // Optional booleans — BE uses Java primitive `boolean`, so always send a real boolean (false when unset)
  glassInsurance: boolean
  winterTyres: boolean
  vignette: boolean

  // Optional — BE accepts null
  vignetteExpirationDate: number | null  // timestamp; meaningful only when vignette === true
  tyreChangingAppointment: number | null // timestamp; never edited in long-term forms
  leasingCompany: string | null
  note: string | null

  customData: LongTermCaseCustomData

  // Resolved relations (populated by store after fetch)
  customerData?: CustomerData
  rentalCarData?: RentalCarData

  // Audit fields
  created?: number
  changed?: number
  createdBy?: string
  changedBy?: string
  ownedBy?: string
}

// ==================== Display Helpers ====================

export const getLongTermCaseClientName = (c: LongTermCase): string => {
  if (!c.customerData) return '-'
  if (c.customerData.name) return c.customerData.name
  const parts = [c.customerData.firstName, c.customerData.surname].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : '-'
}

export const getLongTermCaseCarName = (c: LongTermCase): string => {
  if (!c.rentalCarData) return '-'
  const parts = [c.rentalCarData.brand, c.rentalCarData.type].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : '-'
}
